import { useEffect, useState } from "react";
import { TopBar } from "@/components/PageShell";
import { Link } from "react-router-dom";
import { FileText, MessageCircle, Archive, Send, CheckCircle2, Pencil, Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const COLUMNS = [
  { key: "draft",    label: "Rascunho",  icon: Pencil,      color: "text-slate-600",  bar: "bg-slate-300" },
  { key: "sent",     label: "Enviado",   icon: Send,        color: "text-[#0f4c5c]",  bar: "bg-[#0f4c5c]" },
  { key: "signed",   label: "Assinado",  icon: CheckCircle2,color: "text-emerald-700",bar: "bg-emerald-600" },
  { key: "archived", label: "Arquivado", icon: Archive,     color: "text-slate-500",  bar: "bg-slate-400" },
];

const NEXT_ACTIONS = {
  draft: [{ to: "sent", label: "Marcar como enviado", icon: Send }, { to: "archived", label: "Arquivar", icon: Archive }],
  sent: [{ to: "signed", label: "Marcar como assinado", icon: CheckCircle2 }, { to: "draft", label: "Voltar a rascunho", icon: Pencil }, { to: "archived", label: "Arquivar", icon: Archive }],
  signed: [{ to: "archived", label: "Arquivar", icon: Archive }],
  archived: [{ to: "draft", label: "Reabrir", icon: Pencil }],
};

function waLink(phone, url) {
  const digits = (phone || "").replace(/\D/g, "");
  const num = digits.startsWith("55") ? digits : (digits ? "55" + digits : "");
  const text = encodeURIComponent(`Olá! Este é o link para assinatura do documento: ${url}`);
  return num ? `https://wa.me/${num}?text=${text}` : `https://wa.me/?text=${text}`;
}

function Card({ d, onMove, onShareWa }) {
  const [openMenu, setOpenMenu] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4 hover:shadow-sm transition" data-testid={`kb-card-${d.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-wider text-[#0f4c5c] font-semibold">{d.type.replace(/_/g," ")}</div>
        <div className="text-[10px] text-slate-400">{new Date(d.updated_at).toLocaleDateString('pt-BR')}</div>
      </div>
      <Link to={`/documents/${d.id}`} className="font-heading text-sm font-semibold text-slate-900 hover:underline block truncate">{d.title}</Link>
      {d.client_name && (
        <Link to={`/clients/${d.client_id}`} className="text-xs text-slate-500 mt-1 truncate block hover:text-slate-900">{d.client_name}</Link>
      )}
      <div className="flex items-center gap-2 mt-3">
        <button onClick={() => onShareWa(d)} data-testid={`kb-wa-${d.id}`}
          className="text-[11px] px-2 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 inline-flex items-center gap-1 hover:bg-emerald-100 transition">
          <MessageCircle className="w-3 h-3"/> WhatsApp
        </button>
        <div className="relative ml-auto">
          <button onClick={() => setOpenMenu(v => !v)} data-testid={`kb-move-${d.id}`}
            className="text-[11px] px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50 transition">
            Mover ▾
          </button>
          {openMenu && (
            <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 min-w-[180px] py-1">
              {(NEXT_ACTIONS[d.status] || []).map(a => (
                <button key={a.to} onClick={() => { onMove(d, a.to); setOpenMenu(false); }}
                  className="w-full text-left text-xs px-3 py-2 hover:bg-slate-50 inline-flex items-center gap-2">
                  <a.icon className="w-3.5 h-3.5"/> {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Kanban() {
  const [board, setBoard] = useState({ draft: [], sent: [], signed: [], archived: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/documents-kanban");
    setBoard(r.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const move = async (d, to) => {
    await api.patch(`/documents/${d.id}/status`, { status: to });
    toast.success("Status atualizado");
    load();
  };

  const shareWa = async (d) => {
    try {
      let link = null;
      const r = await api.get(`/documents/${d.id}/share`);
      link = r.data?.token ? r.data : null;
      if (!link) {
        const created = await api.post(`/documents/${d.id}/share`);
        link = created.data;
      }
      const url = `${window.location.origin}/sign/${link.token}`;
      window.open(waLink(d.client_phone, url), "_blank");
      // status became "sent" server-side
      load();
    } catch (e) {
      toast.error("Erro ao gerar link");
    }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="kanban-page">
      <TopBar/>
      <div className="flex-1 p-3 sm:p-6 lg:p-8 font-sans">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Escritório</h1>
          <p className="text-slate-500 mt-1">Acompanhe cada contrato do rascunho ao arquivamento. Todo dia às 9h enviamos um aviso por e-mail para documentos parados há +3 dias.</p>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="btn-digest-now"
            onClick={async () => {
              try { await api.post("/cron/deadline-digest"); toast.success("Aviso de prazo enviado ao seu e-mail"); }
              catch (e) { toast.error("Falha ao enviar aviso"); }
            }}
            className="text-sm px-4 h-10 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center gap-2 transition">
            <Bell className="w-4 h-4"/> Enviar aviso agora
          </button>
          <Link to="/clients" className="text-sm text-[#0f4c5c] hover:underline">Ver clientes →</Link>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-16">Carregando...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {COLUMNS.map(col => (
            <section key={col.key} className="bg-slate-100/60 rounded-2xl p-3" data-testid={`kb-col-${col.key}`}>
              <div className="px-2 py-2 flex items-center gap-2">
                <div className={`w-1.5 h-6 rounded-full ${col.bar}`}/>
                <col.icon className={`w-4 h-4 ${col.color}`}/>
                <div className="font-heading font-semibold text-slate-900">{col.label}</div>
                <span className="ml-auto text-xs text-slate-500 tabular-nums">{board[col.key].length}</span>
              </div>
              <div className="space-y-3 min-h-[100px]">
                {board[col.key].length === 0 ? (
                  <div className="text-xs text-slate-400 text-center py-6">Vazio</div>
                ) : (
                  board[col.key].map(d => <Card key={d.id} d={d} onMove={move} onShareWa={shareWa}/>)
                )}
              </div>
            </section>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
